import type {
  OrderWorkflow,
  OrderWorkflowStage,
  OrderWorkflowSubStep,
  StageRun,
  StageRunStatus,
} from '@nucleus-commerce/api-client/admin';

export const ACTIVE: readonly StageRunStatus[] = ['in_progress', 'waiting_customer'];
export const DONE: readonly StageRunStatus[] = ['completed', 'skipped', 'stopped'];

export type StageAction =
  | 'start'
  | 'complete'
  | 'deliver'
  | 'recordAnswer'
  | 'skip'
  | 'enable'
  | 'due'
  | 'note'
  | 'sendFiles'
  | 'sendReview'
  | 'qc'
  | 'parcels';

/** Outcomes the backend writes that staff should read; plain "done" says nothing more. */
export const KNOWN_OUTCOMES = [
  'confirmed',
  'confirmed_with_changes',
  'not_received',
  'approved',
  'not_answered',
  'not_needed',
  'skipped',
  'closed_with_stage',
] as const;
export type KnownOutcome = (typeof KNOWN_OUTCOMES)[number];

export function outcomeKey(outcome: string | null): KnownOutcome | null {
  return (KNOWN_OUTCOMES as readonly string[]).includes(outcome ?? '')
    ? (outcome as KnownOutcome)
    : null;
}

/** What staff may record for a customer stage they close by hand (the backend's list). */
export const CUSTOMER_OUTCOMES = {
  customer_confirmation: ['confirmed', 'confirmed_with_changes', 'not_received'],
  customer_review: ['approved', 'not_answered'],
} as const;

/** Late right now, or flagged late by the sweep. */
export function isLate(run: StageRun): boolean {
  return run.delayed || (run.delayedAt !== null && !DONE.includes(run.status));
}

/**
 * Closing a late stage needs a delay reason unless one is already on the run (recorded when the
 * due date moved, or by an earlier partial action); the backend accepts those without a new one.
 */
export function needsDelayReason(run: StageRun): boolean {
  return isLate(run) && !run.delayReason;
}

/**
 * Mirrors the backend's ordering rules so only actions that can succeed are offered: top-level
 * stages start in order, sub-steps only while their stage runs, optional stages switch on only
 * before the workflow has moved past them. The backend stays the authority (409
 * WORKFLOW_STAGE_NOT_ACTIVE), so a stale page simply gets the refusal.
 */
export function stageActions(
  workflow: OrderWorkflow,
  stage: OrderWorkflowStage,
  sub?: OrderWorkflowSubStep,
): StageAction[] {
  const run = sub ? sub.run : stage.run;
  const actions: StageAction[] = [];
  const active = workflow.status === 'active';
  const open = !DONE.includes(run.status);

  if (active) {
    const tops = workflow.stages;
    const index = tops.findIndex((s) => s.key === stage.key);
    if (run.status === 'pending') {
      if (sub) {
        if (stage.run.status === 'in_progress') actions.push('start');
      } else {
        const blocked =
          tops.some((s) => ACTIVE.includes(s.run.status)) ||
          tops.slice(0, index).some((s) => s.run.status === 'pending');
        if (!blocked) actions.push('start');
      }
    }
    if (ACTIVE.includes(run.status)) {
      const type = sub ? 'task' : stage.type;
      if (type === 'task') actions.push('complete');
      if (type === 'delivered') actions.push('deliver');
      if (type === 'customer_confirmation') actions.push('recordAnswer');
      if (type === 'send_files' && run.status === 'in_progress') actions.push('sendFiles');
      if (type === 'customer_review') actions.push('sendReview', 'recordAnswer');
      if (type === 'qc' && run.status === 'in_progress') actions.push('qc');
      if (type === 'shipping' && run.status === 'in_progress') actions.push('parcels');
    }
    if (!sub && stage.type === 'qc' && run.status === 'completed') actions.push('qc');
    if (open) actions.push('skip');
    if (run.status === 'skipped') {
      const reachable = sub
        ? !DONE.includes(stage.run.status)
        : !tops
            .slice(index + 1)
            .some((s) => s.run.startedAt !== null || s.run.status === 'completed');
      if (reachable) actions.push('enable');
    }
    if (open) actions.push('due');
  }
  actions.push('note');
  return actions;
}
